import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {HttpClientService} from './http-client.service';
import {map, shareReplay, tap} from 'rxjs/operators';

declare let iNet: any;
declare let $: any;

@Injectable({
    providedIn: 'platform'
})
export class CoreService {
    private static environment = {production: false};
    private $appCache: Observable<Object>;
    private applications: Array<any>;

    constructor(private http: HttpClientService) {

    }

    logout() {
        if(iNet.username) {
            this.http.post(iNet.getPUrl('system/logout'), {'username': iNet.username}).subscribe(
                (data: LogoutData) => {
                    const iFrame = document.createElement('iframe');
                    iFrame.setAttribute('src', data.uuid);
                    iFrame.addEventListener('load', function () {
                        location.reload();
                    });
                    document.body.appendChild(iFrame);
                }, (err: HttpErrorResponse) => {
                }
            );
        }
    }

    /*
    * Gets the avatar URL with the username
    * @deprecated Use UserProfileService.getAvatarUrlByUsername() instead
    */
    getAvatar(usercode: string, thumbnail?: number): string {
        thumbnail = thumbnail || 50;
        return iNet.getPUrl('system/userprofile/photo') +
            `?usercode=${usercode}&thumbnail=${thumbnail}`;
    }

    isExternalUser(): boolean {
        return (iNet.externalUser == 'true');
    }

    isCommunity(): boolean {
        return (this.isExternalUser() || iNet.firmPrefix == 'smartcloud' || iNet.firmPrefix == 'community');
    }

    getOrg(): Observable<Object> {
        return this.http.get(iNet.getPUrl('plugin/organization/list'))
            .pipe(map((res: CustomResponse) => res.elements));
    }

    searchOrgan(params: any = {}): Observable<Object> {
        return this.http.postJSON(iNet.getPUrl('plugin/organization/search'), params)
            .pipe(map((res: CustomResponse) => res.elements));
    }

    getSystemApplication(): Observable<Object> {
        if (this.applications) {
            return of(this.applications);
        }
        if (!this.$appCache) {
            this.$appCache = this.http.postJSON(iNet.getPUrl('system/application/list'))
                .pipe(tap((res: CustomResponse) => this.applications = res.elements))
                .pipe(map((res: CustomResponse) => res.elements))
                .pipe(shareReplay(1));
        }
        return this.$appCache;
    }

    searchFirmAccount(params, callback: Function) {
        this.http.postJSON(iNet.getPUrl('plugin/firmaccount/list'), params).subscribe(
            (data) => {
                callback(data);
            },
            (err) => {
                callback(null, err);
            }
        );
    }

    updateLanguage(lang: string, callback?: Function) {
        this.http.postJSON(iNet.getPUrl('system/userprofile/languageupdate'), {intl: lang}).subscribe(
            (data) => {
                if (callback) {
                    callback(lang);
                }
            }, (err) => {
                if (callback) {
                    callback(lang);
                }
            }
        );
    }

    /**
     * Get the Image URL for the current app
     * @param fileName - the file name
     */
    getImageUrl(fileName: string): string {
        if (!fileName) {
            return '';
        }
        if (fileName.indexOf('/') === 0) {
            return this.getAssetsPath() + fileName;
        }
        return `${this.getAssetsPath()}/${fileName}`;
    }

    /**
     * Build the file URL for the file server request
     * @param fileName - the given file name
     */
    getFileUrl(fileName: string): string {
        if (!fileName) {
            return '';
        }
        if (fileName.indexOf('/') === 0) {
            return this.getFileServerPath() + fileName;
        }
        return `${this.getFileServerPath()}/${fileName}`;
    }

    getSsoRedirect(data: SsoRedirectData): string {
        return iNet.getPUrl('ssorequest/redirect') + '?' + $.param(data);
    }

    convergePlugins(): Observable<any> {
        return this.convergeSearch({converge: 'plugins'});
    }

    convergeSearch(params): Observable<any> {
        return this.http.postJSON(iNet.getPUrl('converge/search'), params);
    }

    setEnvironment(v: any = {production: false}) {
        CoreService.environment = v;
    }

    getEnvironment(): any {
        return CoreService.environment;
    }

    private getPath(path: string = ''): string {
        const lastIndex = path.lastIndexOf('/');
        if (lastIndex === path.length - 1) {
            return path.substring(0, lastIndex);
        }
        return path;
    }

    /**
     * Gets File server path
     */
    getFileServerPath(): string {
        return this.getPath(iNet.fileServer);
    }

    /**
     * Gets assets path of current app
     */
    getAssetsPath(): string {
        return this.getPath(iNet.imgFolder);
    }
}

export interface LogoutData {
    uuid: string;
}

interface CustomResponse extends Response {
    elements: any[];
}

export interface SsoRedirectData {
    url: string;
    application: string;
    redirect?: boolean;
}

export interface PathRedirectData {
    path: string;
    application: string;
    organId?: string;
    firmPrefix?: string;
}
